import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Globe2, Loader2, MonitorSmartphone, Timer, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Metric } from "./AdminShared";

const RANGES = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "all", label: "All time" },
];

const RANGE_DAYS = { today: 1, "7d": 7, "30d": 30, "90d": 90 };

function zeroFillDaily(daily, range, trackingStart) {
  const byDate = Object.fromEntries(daily.map((d) => [d.date, d]));
  const out = [];
  const today = new Date();
  let days = RANGE_DAYS[range];
  if (!days) {
    // all time — from tracking start (capped at 180 days for readability)
    const start = trackingStart ? new Date(trackingStart.slice(0, 10)) : today;
    days = Math.min(180, Math.max(1, Math.floor((today - start) / 86400000) + 1));
  }
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    out.push(byDate[key] || { date: key, visitors: 0, visits: 0 });
  }
  return out;
}

function fmtDuration(sec) {
  if (!sec) return "0s";
  if (sec < 60) return `${Math.round(sec)}s`;
  return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
}

function BarList({ rows, labelKey, valueKey, testId, empty, subLabel }) {
  const max = Math.max(...rows.map((r) => r[valueKey] || 0), 1);
  return (
    <div className="mt-2 space-y-1.5" data-testid={testId}>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-500">{empty}</div>
      ) : (
        rows.map((r, i) => (
          <div key={`${r[labelKey]}-${i}`} className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-slate-700 font-mono">
                {r[labelKey] || "—"}
                {subLabel ? (
                  <span className="text-slate-400"> · {subLabel(r)}</span>
                ) : null}
              </span>
              <span className="font-mono text-slate-900">{r[valueKey]}</span>
            </div>
            <div className="mt-0.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-sky-500"
                style={{ width: `${Math.max(4, (r[valueKey] / max) * 100)}%` }}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export function VisitorAnalytics() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await api.adminAnalytics(range);
        if (!cancelled) setData(d);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range]);

  const totals = data?.totals || {};
  const daily = data ? zeroFillDaily(data.daily || [], range, data.tracking_start) : [];

  return (
    <div data-testid="admin-visitor-analytics" className="mt-10 cre-surface rounded-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Globe2 className="h-4 w-4 text-sky-600" strokeWidth={1.5} />
          <div className="cre-overline text-sky-700">Website visitor analytics</div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="analytics-range-filters">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              data-testid={`analytics-range-${r.value}`}
              onClick={() => setRange(r.value)}
              className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                range === r.value
                  ? "bg-sky-600 text-white border-sky-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {data?.tracking_start ? (
        <div className="mt-1 text-[11px] font-mono text-zinc-500" data-testid="analytics-tracking-start">
          tracking since {data.tracking_start.slice(0, 10)} · consent-gated, first-party, no raw IP stored
        </div>
      ) : null}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 className="h-5 w-5 mx-auto animate-spin text-zinc-400" />
        </div>
      ) : !data || (!data.tracking_start && totals.visits === 0) ? (
        <div className="py-8 text-center text-sm text-zinc-500" data-testid="analytics-empty">
          No visitor data yet. Tracking starts when visitors accept the
          analytics consent banner on public pages.
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Visitors" value={totals.visitors ?? 0} hint="unique" testId="analytics-metric-visitors" />
            <Metric label="Visits" value={totals.visits ?? 0} hint={`${totals.sessions ?? 0} sessions`} testId="analytics-metric-visits" />
            <Metric label="Repeat visitors" value={totals.repeat_visitors ?? 0} hint="≥2 sessions" testId="analytics-metric-repeat" />
            <Metric label="Avg visit duration" value={fmtDuration(totals.avg_visit_duration_seconds)} hint="estimated per page view" testId="analytics-metric-duration" />
          </div>

          {/* Daily visitors chart */}
          <div className="mt-5" data-testid="analytics-daily-chart">
            <div className="cre-overline mb-2">Daily visitors</div>
            {daily.length === 0 ? (
              <div className="text-xs text-zinc-500">No data in this range.</div>
            ) : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0284c7" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#0284c7" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fontFamily: "monospace" }} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 4 }}
                      formatter={(v, name) => [v, name === "visitors" ? "Visitors" : "Visits"]}
                    />
                    <Area type="monotone" dataKey="visits" stroke="#94a3b8" fill="none" strokeWidth={1} strokeDasharray="4 3" dot={daily.length <= 2} />
                    <Area type="monotone" dataKey="visitors" stroke="#0284c7" fill="url(#visGrad)" strokeWidth={2} dot={daily.length <= 2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div data-testid="analytics-top-pages">
              <div className="cre-overline">Top pages</div>
              <BarList rows={data.top_pages} labelKey="path" valueKey="views" testId="analytics-pages-list" empty="No page views yet." />
            </div>
            <div data-testid="analytics-top-countries">
              <div className="cre-overline flex items-center gap-1.5">
                <Globe2 className="h-3 w-3" /> Top countries
              </div>
              <BarList rows={data.top_countries} labelKey="country" valueKey="visits" testId="analytics-countries-list" empty="No geo data yet." />
              {data.top_cities?.length ? (
                <div className="mt-3">
                  <div className="cre-overline">Top cities</div>
                  <div className="mt-1 text-[11px] font-mono text-slate-600 space-y-0.5" data-testid="analytics-cities-list">
                    {data.top_cities.slice(0, 5).map((c, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="truncate">{c.city}, {c.country}</span>
                        <span>{c.visits}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div data-testid="analytics-devices">
              <div className="cre-overline flex items-center gap-1.5">
                <MonitorSmartphone className="h-3 w-3" /> Devices
              </div>
              <BarList rows={data.devices} labelKey="device" valueKey="visits" testId="analytics-devices-list" empty="No device data yet." />
              <div className="mt-3">
                <div className="cre-overline flex items-center gap-1.5">
                  <Timer className="h-3 w-3" /> Browsers
                </div>
                <BarList rows={data.browsers} labelKey="browser" valueKey="visits" testId="analytics-browsers-list" empty="No browser data yet." />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
